import { ArrowUturnLeftIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useRef } from 'react';

import { Icon } from '@/components/core/Icon';
import { Loader } from '@/components/core/Loader';
import {useToast} from '@/hooks/useToast';
import { usePublicAssetUploader } from '@/pages/enhanced-content/enhanced-content.queries';

const VideoNodeViewWrapper = (props: any) => {

    const { toast }  = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isEditable = props.editor.isEditable;

    const { mutate: upload, isPending: uploading} = usePublicAssetUploader({
        onError: () => {
            toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
        },
        onSuccess: (data) => {
            props.updateAttributes({
                src: data[0].url
            });
        }
    });

    const handleSetVideoButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let file = null;

        if (e.target.files) {
            file = e.target.files[0];
        }

        if (file) {
            upload({
                file
            });
        }
    };

    const handleResetButtonClick = () => {
        props.updateAttributes({
            src: props.node.type.defaultAttrs.src,
            title: props.node.type.defaultAttrs.title,
        });
    };

    return (
        <NodeViewWrapper className="tw-relative" contentEditable="false">
            {isEditable && <div
                className="tw-absolute tw-cursor-pointer"
                style={{
                    top: 8,
                    right: 37,
                    zIndex: 9999,
                }}
                contentEditable="false"
                onClick={handleResetButtonClick}
            >
                <Icon
                    icon={ArrowUturnLeftIcon}
                    size='sm'
                    className="tw-text-gray-400"
                />
            </div>}

            {isEditable &&
                (!uploading ?
                    <div
                        className="tw-absolute tw-cursor-pointer"
                        style={{
                            top: 10,
                            right: 8,
                            zIndex: 9999,
                        }}
                        contentEditable="false"
                        onClick={handleSetVideoButtonClick}
                    >
                        <Icon
                            icon={VideoCameraIcon}
                            size='sm'
                            className="tw-text-gray-400"
                        />
                    </div>
                    :
                    <div
                        className="tw-absolute"
                        style={{
                            top: 10,
                            right: 8
                        }}>
                        <Loader />
                    </div>
                )}

            <input ref={fileInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleInputChange} />

            <video width="100%" title={props.node.attrs.title} src={props.node.attrs.src} controls={true} />
        </NodeViewWrapper>
    );
};

const VideoNode = Node.create({
    name: 'video',
    draggable: true,
    selectable: false,
    isolating: true,

    addOptions() {
        return {
            inline: false,
            allowBase64: false,
            HTMLAttributes: {},
        };
    },

    inline() {
        return this.options.inline;
    },

    group() {
        return this.options.inline ? 'inline' : 'block';
    },

    addAttributes() {
        return {
            src: {
                default: 'https://placehold.co/600x400',
            },
            title: {
                default: '',
            },
            width: {
                default: '100%'
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'video',
                getAttrs: (node) => {
                    const element = node as HTMLElement;
                    return {
                        src: element.getAttribute('src'),
                        controls: element.hasAttribute('controls'),
                        width: element.getAttribute('width'),
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const getMimeType = (src:any) => {
            const extension = src.split('.').pop().toLowerCase();
            switch (extension) {
                case 'mp4':
                    return 'video/mp4';
                case 'webm':
                    return 'video/webm';
                case 'ogg':
                    return 'video/ogg';
                case 'mov':
                    return 'video/quicktime';
                default:
                    return 'video/mp4';
            }
        };
        const videoAttributes = {
            ...HTMLAttributes,
            controls: true
        };
        return [
            'video',
            mergeAttributes(this.options.HTMLAttributes, videoAttributes),
            ...HTMLAttributes.src ? [
                ['source', { src: HTMLAttributes.src, type: getMimeType(HTMLAttributes.src) }]
            ] : [],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(VideoNodeViewWrapper);
    }
});

export default VideoNode;
